import React from 'react';
import { User } from 'lucide-react';
import { calculateDistance } from '../utils/geo';
import { detectPlaceCategory } from '../utils/places';

// Place-type emoji map used by the search results dropdown
const PLACE_ICON_MAP = {
  // Food & Drink
  restaurant: '🍽️', cafe: '🍽️', bar: '🍽️', pub: '🍽️', fast_food: '🍽️',
  food_court: '🍽️', ice_cream: '🍽️', bakery: '🍽️',
  // Nightlife
  nightclub: '🌙', club: '🌙',
  // Outdoors
  park: '🌿', garden: '🌿', playground: '🌿', beach: '🌿',
  peak: '🌿', waterfall: '🌿', nature: '🌿',
  // Active
  sports_centre: '⚡', fitness_centre: '⚡', gym: '⚡',
  stadium: '⚡', swimming_pool: '⚡', pitch: '⚡', sports: '⚡',
  // Shopping
  supermarket: '🛍️', convenience: '🛍️', mall: '🛍️',
  marketplace: '🛍️', shop: '🛍️', clothes: '🛍️',
  // Entertainment (catch-all for everything else identifiable)
  museum: '🎭', gallery: '🎭', theatre: '🎭', cinema: '🎭',
  library: '🎭', attraction: '🎭', hotel: '🎭', monument: '🎭',
  castle: '🎭', viewpoint: '🎭', bank: '🎭', pharmacy: '🎭',
  hospital: '🎭', police: '🎭', post_office: '🎭',
  gas_station: '🎭', fuel: '🎭', parking: '🎭',
};

export default function MapHeader({
  // User
  currentUser,
  isOnline,
  // Search
  searchQuery,
  setSearchQuery,
  searchResults,
  setSearchResults,
  isSearching,
  userLocation,
  handleSearch,
  handleResultClick,
  // Refs (for search cache)
  lastSelectionTimeRef,
  ignoreSearchResultsRef,
  lastSearchQueryRef,
  cachedResultsRef,
  // Pin mode
  isPinMode,
  setIsPinMode,
  pinLocation,
  setPinLocation,
  googleMapRef,
  openCreateEventPanelWithCustomLocation,
  // Tools menu
  isToolsMenuOpen,
  setIsToolsMenuOpen,
  // Filters panel
  searchRadius,
  updateRangeCircle,
  loadExistingEvents,
  // Panels
isNotificationsPanelOpen,
  setIsNotificationsPanelOpen,
  notificationCount,
  hasUnreadChats,
  isChatsListPanelOpen,
  onOpenProfile,
}) {

const handleFilterToggle = () => {
    const slider = document.getElementById('rangeSliderPanel');
    if (!slider) return;
    const isOpen = slider.dataset.snap && slider.dataset.snap !== 'closed';
    if (isOpen) {
      slider.dataset.snap = 'closed';
      slider.style.bottom = '0';
      slider.style.transform = 'translateX(-50%) translateY(100%)';
      if (window.rangeCircle) { window.rangeCircle.remove(); window.rangeCircle = null; }
    } else {
      const isMobile = window.innerWidth <= 800;
      slider.style.bottom = isMobile ? 'calc(var(--footer-height, 46px) + 20px + 44px)' : '46px';
      if (isMobile) {
        slider.dataset.snap = 'half';
        slider.style.transform = 'translateX(-50%) translateY(calc(100% - 265px))';
      } else {
        slider.dataset.snap = 'full';
        slider.style.transform = 'translateX(-50%) translateY(0)';
      }
      updateRangeCircle(searchRadius);
      loadExistingEvents(searchRadius);
    }
  };

  const handleEnterPinMode = () => {
    setIsPinMode(true);
    if (googleMapRef.current) {
      const center = googleMapRef.current.getCenter();
      setPinLocation({ lat: center.lat, lng: center.lng });
    }
  };

  const handleConfirmPin = () => {
    if (pinLocation) {
      openCreateEventPanelWithCustomLocation(pinLocation);
      setIsPinMode(false);
      setPinLocation(null);
    }
  };

  const handleCancelPin = () => {
    setIsPinMode(false);
    setPinLocation(null);
  };

  // ── Shared search input behaviour ─────────────────────────────────────────
  const handleSearchChange = (newValue) => {
    window._userIsTypingSearch = true;
    setSearchQuery(newValue);
    if (lastSelectionTimeRef) lastSelectionTimeRef.current = 0;
    if (ignoreSearchResultsRef) ignoreSearchResultsRef.current = false;
    if (lastSearchQueryRef && cachedResultsRef && newValue !== lastSearchQueryRef.current) {
      cachedResultsRef.current = [];
    }
  };

  const handleSearchClick = () => {
    if (lastSelectionTimeRef?.current !== 0) {
      if (Date.now() - lastSelectionTimeRef.current < 300) return;
    }
    if (window.innerWidth <= 800) {
      window.closeAllPanels?.();
      window.closeChatPanel?.();
      window.closeChatsListPanel?.();
      window.closeAllParticipantsPanel?.();
      window.closeUserProfilePanel?.();
      window.setIsProfilePanelOpen?.(false);
    }
    if (ignoreSearchResultsRef) ignoreSearchResultsRef.current = false;
    if (searchQuery.trim().length >= 2) {
      if (searchQuery === lastSearchQueryRef?.current && cachedResultsRef?.current?.length > 0) {
        // use cached
      } else {
        handleSearch();
      }
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    document.querySelectorAll('[data-search-input]').forEach(el => {
      el.style.borderColor = '#e0e0e0';
      el.style.boxShadow = el.dataset.searchShadow || '0 2px 8px rgba(0,0,0,0.1)';
    });
  };

  // ── Reusable sub-components ───────────────────────────────────────────────

  const SearchIcon = ({ size = 20 }) => isSearching ? (
    <div style={{ width: size, height: size, border: `${size > 16 ? 3 : 2}px solid #000`, borderTop: `${size > 16 ? 3 : 2}px solid #4285f4`, borderRadius: '50%', animation: 'spinSearch 0.8s linear infinite' }} />
  ) : (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#5f6368" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path>
    </svg>
  );

  const PinConfirmCancel = () => (
    <div style={{ display: 'flex', gap: 0, alignItems: 'center', background: 'white', border: '2px solid #e0e0e0', borderRadius: '22px', padding: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.1)' }}>
      <button onClick={handleCancelPin}
        style={{ width: '36px', height: '36px', background: 'transparent', border: 'none', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s' }}
        onMouseOver={e => { e.currentTarget.style.background = '#fee'; e.currentTarget.querySelector('svg').style.stroke = '#ea4335'; }}
        onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.querySelector('svg').style.stroke = '#5f6368'; }}
        title="Cancel">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5f6368" strokeWidth="2.5" strokeLinecap="round" style={{ transition: 'stroke 0.2s' }}>
          <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
      <div style={{ width: '1px', height: '24px', background: '#e0e0e0' }}></div>
      <button onClick={handleConfirmPin}
        style={{ width: '36px', height: '36px', background: 'transparent', border: 'none', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s' }}
        onMouseOver={e => { e.currentTarget.style.background = '#e6f4ea'; e.currentTarget.querySelector('svg').style.stroke = '#34a853'; }}
        onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.querySelector('svg').style.stroke = '#5f6368'; }}
        title="Confirm" data-pin-confirm="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5f6368" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'stroke 0.2s' }}>
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      </button>
    </div>
  );

const NotificationBell = () => (
    <button
      data-notification-bell
      onClick={() => setIsNotificationsPanelOpen(!isNotificationsPanelOpen)}
      style={{ width: '44px', height: '44px', background: 'white', border: '2px solid #e0e0e0', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.1)', position: 'relative' }}
      onMouseOver={e => { e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2), 0 3px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.transform = 'scale(1.05)'; }}
      onMouseOut={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'scale(1)'; }}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2c2c2c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
      </svg>
      {notificationCount > 0 && (
        <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: '#ea4335', color: 'white', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', border: '2px solid white', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
          {notificationCount > 99 ? '99+' : notificationCount}
        </span>
      )}
    </button>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      id="map-header"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', borderBottom: '1px dashed #000000', zIndex: 10000, padding: '12px 16px', background: 'white' }}
      onClick={(e) => {
        if (isPinMode && !e.target.closest('[data-pin-confirm]')) { setIsPinMode(false); setPinLocation(null); }
        if (isToolsMenuOpen && !e.target.closest('[data-tools-menu]')) setIsToolsMenuOpen(false);
      }}
    >
      {/* ── LEFT: Profile + Chats + Mobile Search ── */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: 1 }}>

        {/* Profile pill */}
        <div
          onClick={() => { if (isChatsListPanelOpen) window.closeChatsListPanel(); onOpenProfile(); }}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', background: 'white', padding: '6px 14px', borderRadius: '50px', border: '1px solid #e5e7eb', transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
          onMouseOver={e => { e.currentTarget.style.background = '#f1f3f4'; e.currentTarget.style.boxShadow = '0 3px 6px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseOut={e => { e.currentTarget.style.background = '#f8f9fa'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', overflow: 'hidden', border: '2px solid #00a3b7', flexShrink: 0 }}>
            {currentUser?.profile?.profilePicture ? (
              <img src={currentUser.profile.profilePicture} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ width: '100%', height: '100%', background: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <User size={20} style={{ color: '#5f6368' }} />
              </div>
            )}
          </div>
          <div className="profile-name-text" style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#202124', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {currentUser?.profile?.name || 'User'}
            </p>
            <p style={{ margin: 0, fontSize: '11px', color: isOnline ? '#34a853' : '#5f6368', fontWeight: '500' }}>
              ● {isOnline ? 'Online' : 'Offline'}
            </p>
          </div>
        </div>

        {/* Chats button */}
        <button
          className="nav-desktop-btn"
          onClick={() => isChatsListPanelOpen ? window.closeChatsListPanel() : window.openChatsListPanel()}
          style={{ width: '44px', height: '44px', background: 'white', border: '2px solid #e0e0e0', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.1)', position: 'relative' }}
          onMouseOver={e => { e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2), 0 3px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.transform = 'scale(1.05)'; }}
          onMouseOut={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'scale(1)'; }}
          onMouseDown={e => e.currentTarget.style.transform = 'scale(0.95)'}
          onMouseUp={e => e.currentTarget.style.transform = 'scale(1.05)'}
          title="Chats"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2c2c2c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          {hasUnreadChats && (
            <span style={{ position: 'absolute', top: '6px', right: '6px', width: '12px', height: '12px', background: '#ea4335', borderRadius: '50%', border: '2px solid white', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}></span>
          )}
        </button>

        {/* Mobile search bar */}
        <div className="mobile-search-btn" style={{ display: 'none', position: 'relative', maxWidth: '400px', margin: 'auto', flex: 1 }}>
          <input
            type="text" placeholder="Search..." value={searchQuery} data-search-input data-search-shadow="0 2px 6px rgba(0,0,0,0.08)"
            onChange={e => { handleSearchChange(e.target.value); if (e.target.value) { e.target.style.borderColor = '#4285f4'; e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; } else { e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.08)'; } }}
            onClick={handleSearchClick}
            style={{ width: '100%', padding: '10px 35px 10px 38px', border: '2px solid #e0e0e0', borderRadius: '20px', fontSize: '14px', outline: 'none', transition: 'all 0.2s', background: 'white', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}
onFocus={e => {
              if (searchQuery) { e.target.style.borderColor = '#4285f4'; e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; }
              if (cachedResultsRef?.current?.length > 0 && searchQuery === lastSearchQueryRef?.current) {
                if (ignoreSearchResultsRef) ignoreSearchResultsRef.current = true;
                setSearchResults(cachedResultsRef.current);
                setTimeout(() => {
                  if (ignoreSearchResultsRef) ignoreSearchResultsRef.current = false;
                }, 500);
              }
            }}
            onBlur={e => { window._userIsTypingSearch = false; if (!searchQuery) { e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.08)'; } }}
          />
          <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
            <SearchIcon size={16} />
          </div>
          {searchQuery && (
            <button onClick={clearSearch} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5f6368" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
          {/* Search results dropdown */}
          {searchResults.length > 0 && (
            <div style={{ position: 'fixed', top: '78px', left: '8px', right: '8px', width: 'auto', marginTop: '0', background: 'white', border: '2px solid #e0e0e0', borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: '400px', overflowY: 'auto', zIndex: 10001 }}>
              {searchResults.map((result, index) => {
                const category = detectPlaceCategory(result.tags || {}, result.display_name);
                const emoji = PLACE_ICON_MAP[category] || '📍';
                const distance = userLocation
                  ? calculateDistance(userLocation.lat, userLocation.lng, parseFloat(result.lat), parseFloat(result.lon))
                  : null;
                const formatDist = (km) => km === null ? '' : km < 1 ? `${Math.round(km * 1000)}m` : `${km.toFixed(1)}km`;
                return (
                  <div key={index} onClick={() => handleResultClick(result)}
                    style={{ padding: '12px 16px', borderBottom: index < searchResults.length - 1 ? '1px solid #f0f0f0' : 'none', cursor: 'pointer', transition: 'background 0.2s' }}
                    onMouseOver={e => e.currentTarget.style.background = '#f8f9fa'}
                    onMouseOut={e => e.currentTarget.style.background = 'white'}
                  >
                    <div style={{ display: 'flex', alignItems: 'start', gap: '10px' }}>
                      <span style={{ fontSize: '20px', flexShrink: 0 }}>{emoji}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#202124', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {result.display_name.split(',')[0]}
                        </p>
                        <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#5f6368', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {result.address || 'No address'}
                        </p>
                      </div>
                      {distance !== null && (
                        <span style={{ flexShrink: 0, fontSize: '13px', fontWeight: '700', color: '#202124' }}>{formatDist(distance)}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Notification bell - mobile only, right of search */}
        <div className="mobile-notif-btn" style={{ display: 'none', flexShrink: 0 }}>
          <NotificationBell />
        </div>
      </div>

      {/* ── CENTER: Desktop search bar ── */}
      <div className="search-bar-container" style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: '100%', maxWidth: '400px', zIndex: 9999 }}>
        <div style={{ position: 'relative' }} data-search-container>
          <input
            type="text" placeholder="Search places..." value={searchQuery} data-search-input data-search-shadow="0 2px 8px rgba(0,0,0,0.1)"
            onChange={e => { handleSearchChange(e.target.value); if (e.target.value) { e.target.style.borderColor = '#4285f4'; e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; } else { e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'; } }}
            onClick={handleSearchClick}
            style={{ width: '100%', padding: '12px 45px 12px 45px', border: '2px solid #e0e0e0', borderRadius: '24px', fontSize: '14px', outline: 'none', transition: 'all 0.2s', background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
onFocus={e => {
              if (searchQuery) { e.target.style.borderColor = '#4285f4'; e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; }
              if (cachedResultsRef?.current?.length > 0 && searchQuery === lastSearchQueryRef?.current) {
                if (ignoreSearchResultsRef) ignoreSearchResultsRef.current = true;
                setSearchResults(cachedResultsRef.current);
                setTimeout(() => {
                  if (ignoreSearchResultsRef) ignoreSearchResultsRef.current = false;
                }, 500);
              }
            }}
            onBlur={e => { window._userIsTypingSearch = false; if (!searchQuery) { e.target.style.borderColor = '#e0e0e0'; e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'; } }}
          />
          <div style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SearchIcon size={20} />
          </div>

          <style>{`
            @keyframes spinSearch { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            @keyframes searchFadeIn { from { opacity: 0; } to { opacity: 1; } }
@media (max-width: 800px) {
  .search-bar-container { display: none !important; }
  .mobile-search-btn { display: flex !important; }
  .profile-name-text { display: none !important; }
  .nav-right-desktop { display: none !important; }
  .nav-right-mobile { display: flex !important; margin-left: 12px;}
  .nav-desktop-btn { display: none !important; }
  .mobile-tab-bar { display: flex !important; }
  .mobile-notif-btn { display: none !important; }
  .nav-tools-btn { display: none !important; }
}
@media (min-width: 581px) and (max-width: 800px) {
  .search-bar-container { display: none !important; }
  .nav-right-mobile { display: flex !important; }
  .nav-tools-btn { display: none !important; }
}
@media (max-width: 580px) {
  .nav-tools-btn { display: flex !important; }
}
          `}</style>

          {searchQuery && (
            <button onClick={clearSearch}
              style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
              onMouseOver={e => e.currentTarget.style.background = '#f1f3f4'}
              onMouseOut={e => e.currentTarget.style.background = 'transparent'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5f6368" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}

          {/* Search results dropdown */}
          {searchResults.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '8px', background: 'white', border: '2px solid #e0e0e0', borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: '400px', overflowY: 'auto', zIndex: 10001 }}>
              {searchResults.map((result, index) => {
                const category = detectPlaceCategory(result.tags || {}, result.display_name);
                const emoji = PLACE_ICON_MAP[category] || '📍';
                const distance = userLocation
                  ? calculateDistance(userLocation.lat, userLocation.lng, parseFloat(result.lat), parseFloat(result.lon))
                  : null;
                const formatDist = (km) => km === null ? '' : km < 1 ? `${Math.round(km * 1000)}m` : `${km.toFixed(1)}km`;

                return (
                  <div key={index} onClick={() => handleResultClick(result)}
                    style={{ padding: '12px 16px', borderBottom: index < searchResults.length - 1 ? '1px solid #f0f0f0' : 'none', cursor: 'pointer', transition: 'background 0.2s' }}
                    onMouseOver={e => e.currentTarget.style.background = '#f8f9fa'}
                    onMouseOut={e => e.currentTarget.style.background = 'white'}
                  >
                    <div style={{ display: 'flex', alignItems: 'start', gap: '10px' }}>
                      <span style={{ fontSize: '24px', flexShrink: 0 }}>{emoji}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#202124', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {result.display_name.split(',')[0]}
                        </p>
                        <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#5f6368', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {result.address || 'No address'}
                        </p>
                        {result.source === 'overpass' && result.type && (
                          <span style={{ display: 'inline-block', marginTop: '4px', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: '#e8f5e9', color: '#2e7d32', textTransform: 'capitalize' }}>
                            {result.type.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                      {distance !== null && (
                        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', marginLeft: '8px' }}>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: '#202124' }}>{formatDist(distance)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── RIGHT: Desktop ── */}
      <div className="nav-right-desktop" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <NotificationBell />

        {/* Custom Pin button */}
        <button onClick={handleEnterPinMode}
          style={{ width: '44px', height: '44px', background: 'white', border: '2px solid #e0e0e0', borderRadius: '50%', display: isPinMode ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.1)' }}
          onMouseOver={e => { e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2), 0 3px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.transform = 'scale(1.05)'; }}
          onMouseOut={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'scale(1)'; }}
          onMouseDown={e => e.currentTarget.style.transform = 'scale(0.95)'}
          onMouseUp={e => e.currentTarget.style.transform = 'scale(1.05)'}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2c2c2c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
        </button>

        {/* Pin confirm/cancel (desktop) */}
        {isPinMode && <PinConfirmCancel />}

        {/* Filters button */}
        <button data-filter-btn onClick={handleFilterToggle}
          style={{ width: '44px', height: '44px', background: 'white', border: '2px solid #e0e0e0', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.1)', position: 'relative' }}
          onMouseOver={e => { e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2), 0 3px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.transform = 'scale(1.05)'; }}
          onMouseOut={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'scale(1)'; }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2c2c2c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="4" y1="6" x2="20" y2="6"></line><line x1="4" y1="12" x2="20" y2="12"></line><line x1="4" y1="18" x2="20" y2="18"></line>
            <circle cx="16" cy="6" r="2" fill="white" stroke="#2c2c2c" strokeWidth="2"></circle>
            <circle cx="9" cy="12" r="2" fill="white" stroke="#2c2c2c" strokeWidth="2"></circle>
            <circle cx="15" cy="18" r="2" fill="white" stroke="#2c2c2c" strokeWidth="2"></circle>
          </svg>
        </button>
      </div>

      {/* ── RIGHT: Mobile ── */}
      <div className="nav-right-mobile" style={{ display: 'none', gap: '12px', alignItems: 'center' }}>
        <NotificationBell />
      </div>
    </div>
  );
}